import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { WorkpayApiError } from '@workpay/ds-shared/services/apiEnvelope';
import { Document, Page, pdfjs } from 'react-pdf';
import { usePinchZoom } from '@workpay/ds-shared/hooks/usePinchZoom';
import { parsePrefixOptions, stripPrefix, joinPrefixedValue } from '@workpay/ds-shared/utils/fieldPrefix';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { signingService } from '@services/signingService';
import { Button } from '@workpay/ds-shared/components/ui/Button';
import { Modal } from '@workpay/ds-shared/components/ui/Modal';
import { SignatureCapture } from './SignatureCapture';
import { ScanUploader } from './ScanUploader';
import type { SubmitFieldRequest, EnvelopeField, EnvelopeDocument } from '@workpay/ds-shared/types/envelope';

import { SearchableSelect } from './SearchableSelect';
import { COUNTRY_CODES } from '@workpay/ds-shared/utils/countryCodes';
import { CURRENCIES } from '@workpay/ds-shared/utils/currencies';
import styles from './SigningView.module.scss';

type SignatureMode = 'draw' | 'type' | 'upload';

const SIGNATURE_FONTS = [
  { name: 'Dancing Script', value: "'Dancing Script', cursive" },
  { name: 'Great Vibes', value: "'Great Vibes', cursive" },
  { name: 'Pacifico', value: "'Pacifico', cursive" },
  { name: 'Caveat', value: "'Caveat', cursive" },
];

// Worker served from same origin via copyPdfWorkerPlugin in vite.config.ts — no CDN dependency.
// BASE_URL defaults to "/" — respects non-root `base` in vite.config if ever set.
pdfjs.GlobalWorkerOptions.workerSrc = `${import.meta.env.BASE_URL}pdf.worker.${pdfjs.version}.min.mjs`;

const BASE_PDF_WIDTH = 700;

interface SigningViewProps {
  token: string;
}

export function SigningView({ token }: SigningViewProps) {
  // -1 means "no field selected yet" — distinct from idx 0. With this:
  //   - `hasNextUnfilled` (looks for i > currentFieldIdx) matches every field
  //     on initial load, so Next Field is enabled whenever any required field
  //     is unfilled, including when the first unfilled is at idx 0.
  //   - `hasPrevUnfilled` (looks for i < currentFieldIdx) matches nothing on
  //     initial load, so Previous Field is correctly disabled until the user
  //     has navigated at least once.
  // Any click on a field or Next/Prev button sets this to a real index.
  const [currentFieldIdx, setCurrentFieldIdx] = useState(-1);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [signatureFieldId, setSignatureFieldId] = useState<string | null>(null);
  const [docNumPages, setDocNumPages] = useState<Record<string, number>>({});
  const [docPdfUrls, setDocPdfUrls] = useState<Record<string, string>>({});
  const [scanUploaded, setScanUploaded] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [signatureMode, setSignatureMode] = useState<SignatureMode>('draw');
  const [typedSignature, setTypedSignature] = useState('');
  const [selectedFont, setSelectedFont] = useState(SIGNATURE_FONTS[0]?.value ?? "'Dancing Script', cursive");
  const [zoom, setZoom] = useState(1);
  const [fitToWidth, setFitToWidth] = useState(true);
  const [fitToPage, setFitToPage] = useState(false);

  const [containerWidth, setContainerWidth] = useState(0);
  const [activeDocIndex, setActiveDocIndex] = useState(0);
  // Compound key: `${docId}-${pageNumber}`
  const pageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { data: session, isLoading, error } = useQuery({
    queryKey: ['signing-session', token],
    queryFn: () => signingService.loadSession(token),
  });

  const sessionDocuments: EnvelopeDocument[] = useMemo(() => session?.documents ?? [], [session?.documents]);
  const isWetInk = session?.signingMethod === 'WET_INK';

  // Track blob URLs in a ref for proper cleanup
  const docPdfUrlsRef = useRef<Record<string, string>>({});

  // Fetch PDF blobs for all documents
  useEffect(() => {
    if (sessionDocuments.length === 0 || isWetInk) return;

    let cancelled = false;

    async function fetchAllDocs() {
      // Parallel fetch — each doc is an independent GET. Individual failures
      // are swallowed so one broken doc doesn't starve the rest. Blob URLs
      // created for successful fetches even if `cancelled` flips mid-flight
      // are caught by the `cancelled` guard below before we publish, AND by
      // the cleanup effect which revokes everything in `docPdfUrlsRef`.
      const results = await Promise.all(
        sessionDocuments.map(async (doc) => {
          if (!doc.id) return null;
          try {
            const res = await signingService.getDocument(token, doc.id);
            return res.data ? { id: doc.id, blob: res.data } : null;
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;
      // Blob URLs are only created AFTER the cancellation check, so the
      // cancelled path leaks nothing — the Blob objects themselves will be
      // GC'd when results goes out of scope.
      const urls: Record<string, string> = {};
      for (const r of results) {
        if (r) urls[r.id] = URL.createObjectURL(r.blob);
      }
      // Revoke previous URLs before swapping in the new ones.
      Object.values(docPdfUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
      docPdfUrlsRef.current = urls;
      setDocPdfUrls(urls);
    }

    fetchAllDocs();
    return () => {
      cancelled = true;
    };
  }, [sessionDocuments, token, isWetInk]);

  // Cleanup all blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(docPdfUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // Measure container width for fit-to-width
  // Use a callback ref to attach ResizeObserver as soon as the element mounts
  const observerRef = useRef<ResizeObserver | null>(null);
  const scrollContainerCallbackRef = useCallback((el: HTMLDivElement | null) => {
    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    // Store ref for other uses
    (scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;

    if (!el) return;
    setContainerWidth(el.clientWidth);
    observerRef.current = new ResizeObserver(() => {
      setContainerWidth(el.clientWidth);
    });
    observerRef.current.observe(el);

    // Track which document section is most prominently visible.
    // Pick the last section whose top has scrolled to within the upper third
    // of the viewport — the earlier +10px threshold was too tight and could
    // miss by ~20px due to the inter-doc divider being inside the section,
    // leaving the highlight stuck on the previous doc.
    const handleScroll = () => {
      const sections = el.querySelectorAll('[data-doc-section]');
      const containerRect = el.getBoundingClientRect();
      const containerTop = containerRect.top;
      const promotionBand = containerTop + containerRect.height * 0.33;
      let active = 0;
      sections.forEach((sec, i) => {
        const secTop = sec.getBoundingClientRect().top;
        if (secTop <= promotionBand) active = i;
      });
      setActiveDocIndex(active);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
  }, []);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => observerRef.current?.disconnect();
  }, []);

  // Fit-to-page: calculate width so one full page (A4 ratio ~1:1.414) fits in the visible area
  const visibleHeight = containerWidth > 0
    ? Math.min(scrollContainerRef.current?.clientHeight ?? window.innerHeight - 160, window.innerHeight - 160)
    : window.innerHeight - 160;
  const fitPageWidth = visibleHeight > 0 ? (visibleHeight - 16) / 1.414 : 0;

  // Computed page width: fit-to-page > fit-to-width > manual zoom
  const pdfPageWidth = useMemo(() => {
    if (fitToPage && fitPageWidth > 0) return fitPageWidth;
    if (fitToWidth && containerWidth > 0) return containerWidth - 48;
    return BASE_PDF_WIDTH * zoom;
  }, [fitToPage, fitPageWidth, fitToWidth, containerWidth, zoom]);

  // Pinch-to-zoom and Ctrl/Cmd +/- support
  const pdfPageWidthRef = useRef(pdfPageWidth);
  pdfPageWidthRef.current = pdfPageWidth;
  usePinchZoom(scrollContainerRef, {
    getZoom: () => pdfPageWidthRef.current / BASE_PDF_WIDTH,
    onZoom: (z) => { setFitToWidth(false); setFitToPage(false); setZoom(z); },
  });

  // Scale factor for field overlays (fields stored relative to 700px)
  const fieldScale = pdfPageWidth / BASE_PDF_WIDTH;

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (isCcRecipient) {
        await signingService.acknowledge(token);
        return;
      }
      const fields: SubmitFieldRequest[] = Object.entries(fieldValues).map(([fieldId, value]) => ({
        fieldId,
        value,
      }));
      if (fields.length > 0) {
        await signingService.submitFields(token, fields);
      }
      await signingService.completeSigning(token);
    },
  });

  const declineMutation = useMutation({
    mutationFn: () => signingService.declineSigning(token, declineReason),
  });

  const isCcRecipient = session?.recipientRole === 'CC';
  const recipientFields: EnvelopeField[] = useMemo(() => session?.fields ?? [], [session?.fields]);

  // Auto-fill fields with backend values, default values, and DATE_SIGNED
  useEffect(() => {
    if (!recipientFields || recipientFields.length === 0) return;
    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
    setFieldValues((prev) => {
      const updates: Record<string, string> = {};
      recipientFields.forEach((f) => {
        const id = f.id ?? '';
        if (!id || prev[id]) return;
        if (f.fieldType === 'DATE_SIGNED') {
          updates[id] = today;
        } else if (f.value) {
          updates[id] = f.value;
        } else if (f.defaultValue) {
          updates[id] = f.defaultValue;
        }
      });
      if (Object.keys(updates).length === 0) return prev;
      return { ...prev, ...updates };
    });
  }, [recipientFields]);

  const filledCount = useMemo(
    () => recipientFields.filter((f) => f.isReadOnly || !!fieldValues[f.id ?? '']).length,
    [recipientFields, fieldValues],
  );

  const requiredFields = useMemo(
    () => recipientFields.filter((f) => {
      if (!f.isRequired || f.isReadOnly) return false;
      // Defence-in-depth for legacy envelopes: a DROPDOWN with no usable options
      // is un-fillable by the signer; treat it as non-required so the signer is
      // not permanently blocked from Finish Signing. New envelopes are blocked
      // at the author-side gate (TemplateDetail / SigningRequestWizard step 4).
      if (f.fieldType === 'DROPDOWN') {
        const hasOption = (f.dropdownOptions ?? '').split(',').some((o) => o.trim() !== '');
        if (!hasOption) return false;
      }
      return true;
    }),
    [recipientFields],
  );

  // Per-document progress map. Keyed by docId. Counts are over recipient-owned
  // fields only (read-only fields don't count towards "fillable"). `required`
  // is needed-to-proceed; `requiredFilled` tells us if the tab can render a ✓.
  interface DocProgress {
    required: number;
    requiredFilled: number;
    totalFillable: number;
    totalFilled: number;
  }
  const docProgress = useMemo<Map<string, DocProgress>>(() => {
    const byDoc = new Map<string, DocProgress>();
    for (const doc of sessionDocuments) {
      byDoc.set(doc.id ?? '', { required: 0, requiredFilled: 0, totalFillable: 0, totalFilled: 0 });
    }
    for (const f of recipientFields) {
      if (f.isReadOnly) continue;
      const entry = byDoc.get(f.documentId ?? '');
      if (!entry) continue;
      const filled = !!fieldValues[f.id ?? ''];
      entry.totalFillable += 1;
      if (filled) entry.totalFilled += 1;
      if (f.isRequired) {
        entry.required += 1;
        if (filled) entry.requiredFilled += 1;
      }
    }
    return byDoc;
  }, [sessionDocuments, recipientFields, fieldValues]);

  // Scroll to a specific field, centering it in the viewport
  const scrollToField = useCallback((fieldId: string, docId: string, pageNumber: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Try to find the field overlay element directly
    const fieldEl = container.querySelector(`[data-field-id="${fieldId}"]`) as HTMLElement | null;
    if (fieldEl) {
      const containerRect = container.getBoundingClientRect();
      const fieldRect = fieldEl.getBoundingClientRect();
      const stickyHeaderHeight = 44;
      // Center the field in the visible area
      const visibleHeight = containerRect.height - stickyHeaderHeight;
      const scrollTop = container.scrollTop + (fieldRect.top - containerRect.top) - stickyHeaderHeight - (visibleHeight / 2) + (fieldRect.height / 2);
      container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
      return;
    }

    // Fallback: scroll to the page top if field element not found
    const key = `${docId}-${pageNumber}`;
    const pageEl = pageRefs.current.get(key);
    if (pageEl) {
      const containerRect = container.getBoundingClientRect();
      const pageRect = pageEl.getBoundingClientRect();
      const stickyHeaderHeight = 44;
      const scrollTop = container.scrollTop + (pageRect.top - containerRect.top) - stickyHeaderHeight - 8;
      container.scrollTo({ top: scrollTop, behavior: 'smooth' });
    }
  }, []);

  // Navigate to a field: set index + scroll to center it
  const navigateToField = useCallback(
    (idx: number) => {
      setCurrentFieldIdx(idx);
      const field = recipientFields[idx];
      if (field?.documentId) {
        scrollToField(field.id ?? '', field.documentId, field.pageNumber);
      }
    },
    [recipientFields, scrollToField],
  );

  // Scroll to the first page of a specific document. Sets activeDocIndex
  // optimistically so the sidebar highlight follows the click immediately —
  // otherwise the scroll observer only promotes a section when its top
  // crosses containerTop + 10, which can miss by ~20px due to the inter-doc
  // divider and leave the highlight on the previous doc. Natural user scroll
  // still hits the observer and overrides if needed.
  const scrollToDoc = useCallback((docId: string, index: number) => {
    setActiveDocIndex(index);
    const container = scrollContainerRef.current;
    if (!container) return;
    const pageEl = pageRefs.current.get(`${docId}-1`);
    if (!pageEl) return;
    const containerRect = container.getBoundingClientRect();
    const pageRect = pageEl.getBoundingClientRect();
    const scrollTop = container.scrollTop + (pageRect.top - containerRect.top) - 8;
    container.scrollTo({ top: scrollTop, behavior: 'smooth' });
  }, []);

  // Flat across-all-docs traversal. `recipientFields` is ordered by
  // fieldOrder (backend), which is sequenced globally across documents, so
  // walking the flat array naturally crosses document boundaries in order.
  // No wrap-around: when there's no next/prev unfilled required field, the
  // button is disabled so the user knows the traversal is done.
  const goToNextUnfilledField = useCallback(() => {
    const nextIdx = recipientFields.findIndex(
      (f, i) => i > currentFieldIdx && f.isRequired && !fieldValues[f.id ?? ''],
    );
    if (nextIdx !== -1) navigateToField(nextIdx);
  }, [recipientFields, currentFieldIdx, fieldValues, navigateToField]);

  const goToPrevUnfilledField = useCallback(() => {
    for (let i = currentFieldIdx - 1; i >= 0; i--) {
      const f = recipientFields[i];
      if (f && f.isRequired && !fieldValues[f.id ?? '']) {
        navigateToField(i);
        return;
      }
    }
  }, [recipientFields, currentFieldIdx, fieldValues, navigateToField]);

  const hasNextUnfilled = useMemo(
    () => recipientFields.some((f, i) => i > currentFieldIdx && f.isRequired && !fieldValues[f.id ?? '']),
    [recipientFields, currentFieldIdx, fieldValues],
  );

  const hasPrevUnfilled = useMemo(() => {
    for (let i = currentFieldIdx - 1; i >= 0; i--) {
      const f = recipientFields[i];
      if (f && f.isRequired && !fieldValues[f.id ?? '']) return true;
    }
    return false;
  }, [recipientFields, currentFieldIdx, fieldValues]);

  const validateField = useCallback((field: EnvelopeField, value: string): string | null => {
    if (field.fieldType === 'CHECKBOX') return null;
    if (field.fieldType === 'EMAIL' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) return 'Please enter a valid email address';
    }
    if (field.isRequired && (!value || !value.trim())) {
      return 'This field is required';
    }
    return null;
  }, []);

  const handleFieldBlur = useCallback((field: EnvelopeField) => {
    const id = field.id ?? '';
    const value = fieldValues[id] ?? '';
    const error = validateField(field, value);
    setFieldErrors((prev) => {
      if (error) return { ...prev, [id]: error };
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  }, [fieldValues, validateField]);

  const handleFieldValue = useCallback((fieldId: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
    setFieldErrors((prev) => {
      if (prev[fieldId]) {
        const { [fieldId]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const openSignatureCapture = useCallback((fieldId: string) => {
    setSignatureFieldId(fieldId);
    setShowSignatureModal(true);
  }, []);

  const setPageRef = useCallback((docId: string, pageNumber: number, el: HTMLDivElement | null) => {
    const key = `${docId}-${pageNumber}`;
    if (el) {
      pageRefs.current.set(key, el);
    } else {
      pageRefs.current.delete(key);
    }
  }, []);

  // ---------- Render guards ----------

  if (isLoading) {
    return <div className={styles.loading}>Loading signing session...</div>;
  }

  if (error || !session) {
    // Envelope-level terminal states (COMPLETED / VOIDED / EXPIRED / DECLINED)
    // surface here as WP_ENVELOPE_TERMINAL_STATUS (code 43022) from the
    // backend's ensureEnvelopeActive guard — by then the session DTO is
    // unreachable, so we can't render the recipient-status-driven screens
    // below. Distinguish "already complete" from "actually broken link".
    const apiErr = error instanceof WorkpayApiError ? error : null;
    if (apiErr?.code === 43022) {
      return (
        <div className={styles.success}>
          <div className={styles.successIcon}>&#10003;</div>
          <h2>Document Already Completed</h2>
          <p>This document has already been signed and finalised. No further action is required.</p>
        </div>
      );
    }
    return (
      <div className={styles.error}>
        <h2>Unable to load signing session</h2>
        <p>This link may have expired or been revoked.</p>
      </div>
    );
  }

  // Terminal recipient states — block the editor entirely and show a status
  // screen. Covers both the just-submitted case (mutation success) and the
  // refresh-after-action case (backend returns a terminal recipientStatus).
  // Envelope-level terminal states (COMPLETED / VOIDED / EXPIRED / DECLINED
  // at the envelope level) are caught by the backend's ENVELOPE_TERMINAL_STATUS
  // error, which falls into the `error` branch above.
  const rs = session.recipientStatus;
  const isAlreadySigned = (submitMutation.isSuccess && !isCcRecipient) || rs === 'SIGNED';
  const isAlreadyDeclined = declineMutation.isSuccess || rs === 'DECLINED';
  const isScanUploaded = rs === 'SCAN_UPLOADED';
  const isAcknowledged = submitMutation.isSuccess && isCcRecipient;

  if (isAlreadyDeclined) {
    return (
      <div className={styles.declined}>
        <h2>Signing Declined</h2>
        <p>
          {declineMutation.isSuccess
            ? 'You have declined to sign this document.'
            : `${session.recipientName}, you previously declined to sign this document.`}
        </p>
      </div>
    );
  }

  if (isAlreadySigned) {
    return (
      <div className={styles.success}>
        <div className={styles.successIcon}>&#10003;</div>
        <h2>
          {submitMutation.isSuccess ? 'Document Signed Successfully' : 'Document Already Signed'}
        </h2>
        <p>
          {submitMutation.isSuccess
            ? `Thank you, ${session.recipientName}. Your signature has been recorded.`
            : `${session.recipientName}, you have already signed this document. No further action is required.`}
        </p>
      </div>
    );
  }

  if (isScanUploaded) {
    return (
      <div className={styles.success}>
        <div className={styles.successIcon}>&#10003;</div>
        <h2>Signed Document Uploaded</h2>
        <p>
          {session.recipientName}, your signed scan has been received. No further action is required.
        </p>
      </div>
    );
  }

  if (isAcknowledged) {
    return (
      <div className={styles.success}>
        <div className={styles.successIcon}>&#10003;</div>
        <h2>Document Acknowledged</h2>
        <p>Thank you, {session.recipientName}. Your acknowledgement has been recorded.</p>
      </div>
    );
  }

  // ---------- Field overlay helpers ----------

  const getFieldOverlayClass = (field: EnvelopeField, idx: number): string => {
    const classes = [styles.fieldOverlay];
    if (field.isReadOnly) {
      classes.push(styles.fieldOverlayLocked);
      return classes.join(' ');
    }
    if (idx === currentFieldIdx) classes.push(styles.fieldOverlayActive);
    const id = field.id ?? '';
    if (fieldErrors[id]) {
      classes.push(styles.fieldOverlayError);
    } else if (fieldValues[id]) {
      classes.push(styles.fieldOverlayFilled);
    } else if (field.isRequired) {
      classes.push(styles.fieldOverlayRequired);
    }
    return classes.join(' ');
  };

  const fieldsOnPage = (docId: string, pageNumber: number) =>
    recipientFields
      .map((field, idx) => ({ field, idx }))
      .filter(({ field }) => field.documentId === docId && field.pageNumber === pageNumber);

  // ---------- Render field overlay ----------
  const renderFieldOverlay = (field: EnvelopeField, idx: number) => {
    const id = field.id ?? '';
    const value = fieldValues[id] ?? '';
    const ft = field.fieldType;
    const isLocked = field.isReadOnly ?? false;

    return (
      <div
        key={id || idx}
        data-field-id={id}
        className={getFieldOverlayClass(field, idx)}
        style={{
          left: field.xPosition * fieldScale,
          top: field.yPosition * fieldScale,
          width: field.width * fieldScale,
          height: field.height * fieldScale,
        }}
        onClick={(e) => {
          e.stopPropagation();
          setCurrentFieldIdx(idx);
        }}
      >
        {isLocked ? (
          <div className={styles.lockedField}>
            {ft === 'CHECKBOX' ? (
              <input
                type="checkbox"
                className={styles.inlineCheckbox}
                checked={(value || field.defaultValue) === 'true'}
                disabled
              />
            ) : (
              <span className={styles.lockedValue}>
                {value || field.defaultValue || ''}
              </span>
            )}
            <svg className={styles.lockIcon} width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
              <path d="M11 7V5a3 3 0 0 0-6 0v2H4a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-1zm-1 0H6V5a2 2 0 1 1 4 0v2z"/>
            </svg>
          </div>
        ) : (ft === 'SIGNATURE' || ft === 'INITIALS') ? (
          value && value.startsWith('data:image') ? (
            <img
              src={value}
              alt={ft}
              className={styles.inlineSignatureImg}
              onClick={(e) => { e.stopPropagation(); openSignatureCapture(id); }}
            />
          ) : (
            <button
              className={styles.inlineSignatureBtn}
              onClick={(e) => { e.stopPropagation(); openSignatureCapture(id); }}
            >
              {ft === 'SIGNATURE' ? 'Sign Here' : 'Initial Here'}
            </button>
          )
        ) : ft === 'CHECKBOX' ? (
          <input
            type="checkbox"
            className={styles.inlineCheckbox}
            checked={value === 'true'}
            onChange={(e) => { e.stopPropagation(); handleFieldValue(id, String(e.target.checked)); }}
          />
        ) : ft === 'DATE_SIGNED' ? (
          <span className={styles.inlineReadonly}>
            {value || new Date().toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}
          </span>
        ) : ft === 'DATE' ? (
          <input
            type="date"
            className={styles.inlineInput}
            value={value || field.defaultValue || ''}
            onChange={(e) => handleFieldValue(id, e.target.value)}
            onBlur={() => handleFieldBlur(field)}
            onClick={(e) => e.stopPropagation()}
          />
        ) : ft === 'DROPDOWN' ? (
          <select
            className={styles.inlineInput}
            value={value || field.defaultValue || ''}
            onChange={(e) => handleFieldValue(id, e.target.value)}
            onBlur={() => handleFieldBlur(field)}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="">Select...</option>
            {(field.dropdownOptions ?? '').split(',').map((opt) => {
              const trimmed = opt.trim();
              return trimmed ? <option key={trimmed} value={trimmed}>{trimmed}</option> : null;
            })}
          </select>
        ) : ft === 'NUMBER' ? (
          <input
            type="number"
            className={styles.inlineInput}
            value={value || field.defaultValue || ''}
            onChange={(e) => handleFieldValue(id, e.target.value)}
            onBlur={() => handleFieldBlur(field)}
            onClick={(e) => e.stopPropagation()}
            placeholder={field.label || '0'}
          />
        ) : ft === 'CURRENCY' ? (() => {
          const { prefix: defaultCur, locked: prefixLocked } = parsePrefixOptions(field.dropdownOptions, 'USD');
          const curValue = fieldValues[`${id}__currency`] ?? defaultCur;
          return (
          <div className={styles.inlineCurrency} onClick={(e) => e.stopPropagation()}>
            {prefixLocked ? (
              <span className={styles.lockedPrefix} title="Currency locked by sender">
                <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor"><path d="M11 7V5a3 3 0 0 0-6 0v2H4a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-1zm-1 0H6V5a2 2 0 1 1 4 0v2z"/></svg>
                {curValue}
              </span>
            ) : (
              <SearchableSelect
                compact
                options={CURRENCIES}
                value={curValue}
                onChange={(cur) => {
                  handleFieldValue(`${id}__currency`, cur);
                  const amt = stripPrefix('CURRENCY', value);
                  handleFieldValue(id, joinPrefixedValue(cur, amt));
                }}
                placeholder="Search currency..."
              />
            )}
            <input
              type="number"
              step="0.01"
              className={styles.inlineInput}
              value={stripPrefix('CURRENCY', value) || field.defaultValue || ''}
              onChange={(e) => {
                handleFieldValue(id, joinPrefixedValue(curValue, e.target.value));
              }}
              onBlur={() => handleFieldBlur(field)}
              placeholder="0.00"
            />
          </div>
          );
        })() : ft === 'PHONE' ? (() => {
          const { prefix: defaultCode, locked: prefixLocked } = parsePrefixOptions(field.dropdownOptions, '+1');
          const codeValue = fieldValues[`${id}__code`] ?? defaultCode;
          return (
          <div className={styles.inlinePhone} onClick={(e) => e.stopPropagation()}>
            {prefixLocked ? (
              <span className={styles.lockedPrefix} title="Country code locked by sender">
                <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor"><path d="M11 7V5a3 3 0 0 0-6 0v2H4a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-1zm-1 0H6V5a2 2 0 1 1 4 0v2z"/></svg>
                {codeValue}
              </span>
            ) : (
              <SearchableSelect
                compact
                options={COUNTRY_CODES}
                value={codeValue}
                onChange={(code) => {
                  handleFieldValue(`${id}__code`, code);
                  const num = stripPrefix('PHONE', value);
                  handleFieldValue(id, joinPrefixedValue(code, num));
                }}
                placeholder="Search country..."
              />
            )}
            <input
              type="tel"
              className={styles.inlineInput}
              value={stripPrefix('PHONE', value) || field.defaultValue || ''}
              onChange={(e) => {
                handleFieldValue(id, joinPrefixedValue(codeValue, e.target.value));
              }}
              onBlur={() => handleFieldBlur(field)}
              placeholder="Phone number"
            />
          </div>
          );
        })() : (
          <input
            type={ft === 'EMAIL' ? 'email' : 'text'}
            className={styles.inlineInput}
            value={value || field.defaultValue || ''}
            onChange={(e) => handleFieldValue(id, e.target.value)}
            onBlur={() => handleFieldBlur(field)}
            onClick={(e) => e.stopPropagation()}
            placeholder={field.label || ft.toLowerCase()}
          />
        )}
      </div>
    );
  };

  // ---------- Render ----------

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            {isCcRecipient ? 'Review document' : session.subject}
          </h1>
          <span className={styles.recipientInfo}>
            {isCcRecipient
              ? `Viewing as ${session.recipientName} (CC)`
              : `Signing as ${session.recipientName}`}
          </span>
        </div>
        <div className={styles.headerRight}>
          <Button variant="ghost" size="sm" onClick={() => setShowDeclineModal(true)}>
            Decline
          </Button>
        </div>
      </header>

      <div className={styles.body}>
        {isWetInk ? (
          <div className={styles.wetInkFlow}>
            <div className={styles.wetInkCard}>
              <h2>Print &amp; Sign</h2>
              <p>
                {sessionDocuments.length > 1
                  ? `This envelope contains ${sessionDocuments.length} documents that require physical signatures.`
                  : 'This document requires a physical signature.'}{' '}
                Please:
              </p>
              <ol className={styles.wetInkSteps}>
                <li>Download {sessionDocuments.length > 1 ? 'all documents' : 'the document'}</li>
                <li>Print and sign {sessionDocuments.length > 1 ? 'each document' : 'it'}</li>
                <li>Scan the signed {sessionDocuments.length > 1 ? 'documents' : 'document'}</li>
                <li>Upload the scanned {sessionDocuments.length > 1 ? 'copies' : 'copy'}</li>
              </ol>

              {sessionDocuments.length > 1 && (
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
                  Documents to sign ({sessionDocuments.length})
                </h3>
              )}
              <div className={styles.wetInkDocList}>
                {sessionDocuments.map((doc, i) => (
                  <div key={doc.id} className={styles.wetInkDocItem}>
                    <div className={styles.wetInkDocInfo}>
                      <span className={styles.wetInkDocOrder}>{i + 1}</span>
                      <span className={styles.wetInkDocName}>{doc.name || 'Document'}</span>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        if (doc.id) {
                          signingService.downloadDocument(token, doc.id).then((res) => {
                            const url = URL.createObjectURL(res.data);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = doc.name || 'document.pdf';
                            a.click();
                            URL.revokeObjectURL(url);
                          });
                        }
                      }}
                    >
                      Download
                    </Button>
                  </div>
                ))}
              </div>

              <div className={styles.wetInkActions}>
                <ScanUploader
                  token={token}
                  onUploadComplete={() => setScanUploaded(true)}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.electronicFlow}>
            {/* Toolbar: progress + zoom controls */}
            <div className={styles.pageNav}>
              {!isCcRecipient && recipientFields.length > 0 && (
                <span>{filledCount} of {recipientFields.length} fields completed</span>
              )}

              <span className={styles.zoomDivider}>|</span>

              <button className={`${styles.fitBtn} ${fitToPage ? styles.fitBtnActive : ''}`} onClick={() => { setFitToPage(true); setFitToWidth(false); }}>Fit Page</button>
              <button className={`${styles.fitBtn} ${fitToWidth ? styles.fitBtnActive : ''}`} onClick={() => { setFitToWidth(true); setFitToPage(false); }}>Fit Width</button>

              <span className={styles.zoomDivider}>|</span>

              <button onClick={() => { const step = window.innerWidth <= 767 ? 0.25 : 0.1; const cur = pdfPageWidth / BASE_PDF_WIDTH; setFitToWidth(false); setFitToPage(false); setZoom(Math.max(0.3, +(cur - step).toFixed(2))); }}>&#8722;</button>
              <span>{Math.round((pdfPageWidth / BASE_PDF_WIDTH) * 100)}%</span>
              <button onClick={() => { const step = window.innerWidth <= 767 ? 0.25 : 0.1; const cur = pdfPageWidth / BASE_PDF_WIDTH; setFitToWidth(false); setFitToPage(false); setZoom(Math.min(3, +(cur + step).toFixed(2))); }}>+</button>
            </div>

            {/* Body: doc sidebar (multi-doc only) + PDF preview side-by-side.
                Sidebar lets the signer jump between docs and see per-doc
                progress. Active item syncs with the scroll observer below.
                On single-doc the sidebar is omitted — the preview takes the
                full body width. */}
            <div className={styles.electronicBody}>
              {sessionDocuments.length > 1 && (
                <aside className={styles.docSidebar} aria-label="Documents">
                  <div className={styles.docSidebarTitle}>
                    Documents <span className={styles.docSidebarCount}>({sessionDocuments.length})</span>
                  </div>
                  <ul className={styles.docList} role="tablist">
                    {sessionDocuments.map((doc, i) => {
                      const docId = doc.id ?? '';
                      const progress = docProgress.get(docId);
                      const isActive = i === activeDocIndex;
                      const isComplete = !!progress && progress.required > 0 && progress.requiredFilled === progress.required;
                      const pages = docNumPages[docId] ?? 0;
                      const itemClasses = [
                        styles.docItem,
                        isActive ? styles.docItemActive : '',
                        isComplete ? styles.docItemComplete : '',
                      ].filter(Boolean).join(' ');
                      return (
                        <li key={docId || i}>
                          <button
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            className={itemClasses}
                            onClick={() => scrollToDoc(docId, i)}
                            title={doc.name || `Document ${i + 1}`}
                          >
                            <span className={styles.docOrder}>{i + 1}</span>
                            <span className={styles.docInfo}>
                              <span className={styles.docName}>{doc.name || `Document ${i + 1}`}</span>
                              <span className={styles.docMeta}>
                                {pages > 0 ? `${pages} page${pages !== 1 ? 's' : ''}` : ''}
                                {progress && progress.required > 0 && (
                                  <>
                                    {pages > 0 ? ' · ' : ''}
                                    <span className={styles.docProgress}>
                                      {isComplete ? '✓ Complete' : `${progress.requiredFilled}/${progress.required} filled`}
                                    </span>
                                  </>
                                )}
                              </span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </aside>
              )}

              {/* PDF Document Preview — all documents in continuous scroll */}
              <div className={styles.documentPreview} ref={scrollContainerCallbackRef}>
              {sessionDocuments.map((doc, docIndex) => {
                const docId = doc.id ?? '';
                const pdfUrl = docPdfUrls[docId];
                const numPages = docNumPages[docId] ?? 0;
                const isMultiDoc = sessionDocuments.length > 1;

                return (
                  <div key={docId} className={styles.documentSection} data-doc-section>
                    {/* Divider between documents */}
                    {isMultiDoc && docIndex > 0 && (
                      <div className={styles.documentDivider} />
                    )}

                    {pdfUrl ? (
                      <Document
                        file={pdfUrl}
                        onLoadSuccess={({ numPages: n }) => {
                          setDocNumPages((prev) => ({ ...prev, [docId]: n }));
                        }}
                        loading={<div className={styles.pdfLoading}>Loading document...</div>}
                        error={<div className={styles.pdfError}>Failed to load document.</div>}
                      >
                        {Array.from({ length: numPages }, (_, i) => {
                          const pageNumber = i + 1;
                          const pageFields = fieldsOnPage(docId, pageNumber);
                          return (
                            <div
                              key={`${docId}-${pageNumber}`}
                              className={styles.pageWrapper}
                              ref={(el) => setPageRef(docId, pageNumber, el)}
                            >
                              <Page pageNumber={pageNumber} width={pdfPageWidth} />

                              {/* Inline editable field overlays */}
                              {pageFields.map(({ field, idx }) => renderFieldOverlay(field, idx))}

                              {pageNumber < numPages && <div className={styles.pageSeparator} />}
                            </div>
                          );
                        })}
                      </Document>
                    ) : (
                      <div className={styles.pdfLoading}>Loading document...</div>
                    )}
                  </div>
                );
              })}
              </div>
            </div>
          </div>
        )}
      </div>

      <footer className={styles.footer}>
        <div className={styles.footerInfo}>
          {!isWetInk && !isCcRecipient && recipientFields.length > 0 && (() => {
            const activeDocId = sessionDocuments[activeDocIndex]?.id ?? '';
            const activeProgress = docProgress.get(activeDocId);
            if (sessionDocuments.length > 1 && activeProgress && activeProgress.required > 0) {
              return (
                <span>
                  Doc {activeDocIndex + 1} of {sessionDocuments.length}: {activeProgress.requiredFilled}/{activeProgress.required} required filled
                  <span className={styles.footerInfoAggregate}>
                    {' '}· {filledCount}/{recipientFields.length} total
                  </span>
                </span>
              );
            }
            return (
              <span>
                {filledCount} of {recipientFields.length} fields completed
              </span>
            );
          })()}
        </div>
        <div className={styles.footerActions}>
          {!isWetInk && !isCcRecipient && recipientFields.length > 0 && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={goToPrevUnfilledField}
                disabled={!hasPrevUnfilled}
              >
                Previous Field
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={goToNextUnfilledField}
                disabled={!hasNextUnfilled}
              >
                Next Field
              </Button>
            </>
          )}
          {!isCcRecipient && (
            <Button variant="ghost" onClick={() => setShowDeclineModal(true)}>
              Decline
            </Button>
          )}
          <Button
            loading={submitMutation.isPending}
            onClick={() => setShowConfirmModal(true)}
            disabled={
              isCcRecipient
                ? false
                : isWetInk
                  ? !scanUploaded
                  : requiredFields.some((f) => !fieldValues[f.id ?? ''])
            }
          >
            {isCcRecipient ? 'Acknowledge' : isWetInk ? 'Complete' : 'Finish Signing'}
          </Button>
        </div>
      </footer>

      {/* Signature capture modal */}
      <Modal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        title="Add Your Signature"
        size="md"
      >
        <div>
          {/* Signature mode tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: '16px' }}>
            {(['draw', 'type', 'upload'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setSignatureMode(mode)}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  background: 'none',
                  borderBottom: signatureMode === mode ? '2px solid #3b82f6' : '2px solid transparent',
                  color: signatureMode === mode ? '#3b82f6' : '#6b7280',
                  fontWeight: signatureMode === mode ? 600 : 400,
                  cursor: 'pointer',
                  fontSize: '14px',
                  textTransform: 'capitalize',
                }}
              >
                {mode}
              </button>
            ))}
          </div>

          {signatureMode === 'draw' && (
            <SignatureCapture
              onSave={(dataUrl) => {
                if (signatureFieldId) {
                  handleFieldValue(signatureFieldId, dataUrl);
                }
                setShowSignatureModal(false);
              }}
              onCancel={() => setShowSignatureModal(false)}
            />
          )}

          {signatureMode === 'type' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input
                type="text"
                placeholder="Type your name"
                value={typedSignature}
                onChange={(e) => setTypedSignature(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              />
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {SIGNATURE_FONTS.map((font) => (
                  <button
                    key={font.name}
                    onClick={() => setSelectedFont(font.value)}
                    style={{
                      padding: '6px 12px',
                      border: selectedFont === font.value ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      borderRadius: '6px',
                      background: selectedFont === font.value ? '#eff6ff' : '#fff',
                      cursor: 'pointer',
                      fontFamily: font.value,
                      fontSize: '14px',
                    }}
                  >
                    {font.name}
                  </button>
                ))}
              </div>
              {typedSignature && (
                <div
                  style={{
                    padding: '20px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    textAlign: 'center',
                    fontFamily: selectedFont,
                    fontSize: '32px',
                    minHeight: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fff',
                  }}
                >
                  {typedSignature}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <Button variant="secondary" size="sm" onClick={() => setShowSignatureModal(false)}>Cancel</Button>
                <Button
                  size="sm"
                  disabled={!typedSignature.trim()}
                  onClick={() => {
                    const canvas = document.createElement('canvas');
                    canvas.width = 500;
                    canvas.height = 200;
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                      ctx.fillStyle = '#fff';
                      ctx.fillRect(0, 0, 500, 200);
                      ctx.fillStyle = '#000';
                      ctx.font = `48px ${selectedFont}`;
                      ctx.textAlign = 'center';
                      ctx.textBaseline = 'middle';
                      ctx.fillText(typedSignature, 250, 100);
                    }
                    const dataUrl = canvas.toDataURL('image/png');
                    if (signatureFieldId) {
                      handleFieldValue(signatureFieldId, dataUrl);
                    }
                    setShowSignatureModal(false);
                    setTypedSignature('');
                  }}
                >
                  Apply Signature
                </Button>
              </div>
            </div>
          )}

          {signatureMode === 'upload' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div
                style={{
                  border: '2px dashed #e5e7eb',
                  borderRadius: '8px',
                  padding: '32px',
                  textAlign: 'center',
                  color: '#6b7280',
                }}
              >
                <p style={{ marginBottom: '8px', fontSize: '14px' }}>Upload a signature image (PNG or JPG)</p>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      const dataUrl = reader.result as string;
                      if (signatureFieldId) {
                        handleFieldValue(signatureFieldId, dataUrl);
                      }
                      setShowSignatureModal(false);
                    };
                    reader.readAsDataURL(file);
                  }}
                  style={{ fontSize: '14px' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="secondary" size="sm" onClick={() => setShowSignatureModal(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Confirmation modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirm Action"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button
              loading={submitMutation.isPending}
              onClick={() => {
                setShowConfirmModal(false);
                submitMutation.mutate();
              }}
            >
              Confirm
            </Button>
          </>
        }
      >
        <p style={{ fontSize: '14px', color: '#4b5563' }}>
          {isCcRecipient
            ? 'Are you sure you want to acknowledge receipt of this document?'
            : session?.recipientRole === 'IN_PERSON_SIGNER'
              ? 'Are you sure you want to submit your signed document?'
              : `Are you sure you want to complete signing${sessionDocuments.length > 1 ? ` all ${sessionDocuments.length} documents` : ''}? This action cannot be undone.`}
        </p>
      </Modal>

      {/* Decline modal */}
      <Modal
        isOpen={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        title="Decline to Sign"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowDeclineModal(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={declineMutation.isPending}
              onClick={() => declineMutation.mutate()}
              disabled={!declineReason}
            >
              Decline
            </Button>
          </>
        }
      >
        <p style={{ marginBottom: '12px', fontSize: '14px', color: '#4b5563' }}>
          Please provide a reason for declining:
        </p>
        <textarea
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            minHeight: '80px',
            fontSize: '14px',
          }}
          value={declineReason}
          onChange={(e) => setDeclineReason(e.target.value)}
          placeholder="Reason for declining..."
        />
      </Modal>
    </div>
  );
}
